/**
 * O balanço do fim do ciclo (etapa 16 do Conselheiro): o que os 12 meses
 * mudaram, em números, para o Principal escrever o parecer e recomendar a
 * renovação, e para os sócios lerem quando ele publicar.
 *
 * Puro: quem chama (`src/lib/conselheiro-balanco.ts`) lê o banco e passa os
 * fatos. Publicado, o resultado fica congelado no balanço.
 */

import { TEMAS, NOME_DA_META, TIPOS_DE_META, decisaoAtrasada, foraDaFaixa, mesDoCiclo, progressoDaMeta } from './conselheiro.js';

/** A partir deste mês do ciclo o Principal escreve o balanço. */
export const MES_DO_BALANCO = 11;
/** Quantos dias antes do fim do ciclo a gestão é chamada a decidir a renovação. */
export const DIAS_PARA_DECIDIR_A_RENOVACAO = 60;

export const RECOMENDACOES = ['RENOVAR', 'RENOVAR_COM_AJUSTES', 'ENCERRAR'] as const;
export type Recomendacao = (typeof RECOMENDACOES)[number];
export const NOME_DA_RECOMENDACAO: Record<Recomendacao, string> = {
  RENOVAR: 'Renovar',
  RENOVAR_COM_AJUSTES: 'Renovar com ajustes',
  ENCERRAR: 'Encerrar',
};

/** O que a gestão decidiu; o agendador cumpre no fim do ciclo. */
export const DECISOES_DE_RENOVACAO = ['RENOVAR', 'ENCERRAR'] as const;
export type DecisaoDeRenovacao = (typeof DECISOES_DE_RENOVACAO)[number];

export function balancoAberto(inicio: Date, agora = new Date()): boolean {
  return mesDoCiclo(inicio, agora) >= MES_DO_BALANCO;
}

export type FatosDoBalanco = {
  inicio: Date;
  fim: Date | null;
  agora: Date;
  /** Notas 0–10 por tema; null se o radar não foi fechado. */
  radar_partida: number[] | null;
  /** O radar final; sem ele, o último de marco fechado. */
  radar_final: number[] | null;
  radar_final_tipo: 'FINAL' | 'MARCO' | null;
  metas: { tipo: string; descricao: string; unidade: string | null; linha_base: number | null; alvo: number | null; atual: number | null }[];
  decisoes: { status: string; prazo: Date | null; concluida_em: Date | null }[];
  indicadores: {
    nome: string;
    unidade: string | null;
    faixa_min: number | null;
    faixa_max: number | null;
    /** Só os do período do ciclo, em qualquer ordem. */
    valores: { competencia: Date; valor: number }[];
  }[];
  manhas: { status: string }[];
  /** Notas de 1 a 5 dos sócios às manhãs do ciclo. */
  avaliacoes: number[];
};

export type NumerosDoBalanco = ReturnType<typeof montarBalanco>;

const media = (xs: number[]) => (xs.length ? Math.round((xs.reduce((s, x) => s + x, 0) / xs.length) * 10) / 10 : null);
const uma = (x: number) => Math.round(x * 10) / 10;

export function montarBalanco(f: FatosDoBalanco) {
  const temas = TEMAS.map((nome, tema) => {
    const partida = f.radar_partida?.[tema] ?? null;
    const final = f.radar_final?.[tema] ?? null;
    return { tema, nome, partida, final, delta: partida != null && final != null ? uma(final - partida) : null };
  });
  const comDelta = temas.filter((t) => t.delta != null) as (typeof temas[number] & { delta: number })[];

  const metas = TIPOS_DE_META.flatMap((tipo) => {
    const m = f.metas.find((x) => x.tipo === tipo);
    if (!m) return [];
    const progresso = progressoDaMeta(m);
    return [{ tipo, nome: NOME_DA_META[tipo], descricao: m.descricao, unidade: m.unidade, linha_base: m.linha_base, alvo: m.alvo, atual: m.atual, progresso, atingida: progresso === 1 }];
  });

  const validas = f.decisoes.filter((d) => d.status !== 'CANCELADA');
  const concluidas = validas.filter((d) => d.status === 'CONCLUIDA');
  const noPrazo = concluidas.filter((d) => !d.prazo || !d.concluida_em || d.concluida_em.getTime() <= d.prazo.getTime());

  const indicadores = f.indicadores.flatMap((i) => {
    const vs = [...i.valores].sort((a, b) => a.competencia.getTime() - b.competencia.getTime());
    const primeiro = vs[0];
    const ultimo = vs[vs.length - 1];
    if (!primeiro || !ultimo) return [];
    return [{
      nome: i.nome,
      unidade: i.unidade,
      primeiro: { competencia: primeiro.competencia, valor: primeiro.valor },
      ultimo: { competencia: ultimo.competencia, valor: ultimo.valor },
      meses: vs.length,
      dentro_da_faixa: !foraDaFaixa(ultimo.valor, i.faixa_min, i.faixa_max),
    }];
  });

  return {
    periodo: { inicio: f.inicio, fim: f.fim, mes: mesDoCiclo(f.inicio, f.agora) },
    radar: {
      partida: media(f.radar_partida ?? []),
      final: media(f.radar_final ?? []),
      final_tipo: f.radar_final_tipo,
      temas,
      mais_avancaram: [...comDelta].filter((t) => t.delta > 0).sort((a, b) => b.delta - a.delta).slice(0, 3).map((t) => t.nome),
      recuaram: comDelta.filter((t) => t.delta < 0).sort((a, b) => a.delta - b.delta).map((t) => t.nome),
    },
    metas,
    decisoes: {
      total: f.decisoes.length,
      concluidas: concluidas.length,
      no_prazo: noPrazo.length,
      canceladas: f.decisoes.length - validas.length,
      em_aberto: validas.length - concluidas.length,
      atrasadas: validas.filter((d) => decisaoAtrasada(d, f.agora)).length,
      /** Concluídas sobre as que não foram canceladas, em %. */
      cumprimento: validas.length ? Math.round((concluidas.length / validas.length) * 100) : null,
    },
    indicadores,
    manhas: {
      realizadas: f.manhas.filter((m) => m.status === 'REALIZADA').length,
      canceladas: f.manhas.filter((m) => m.status === 'CANCELADA').length,
      nota_media: media(f.avaliacoes),
      avaliacoes: f.avaliacoes.length,
    },
  };
}

/** Por que o Principal não pode publicar ainda; null se pode. */
export function motivoParaNaoPublicar(b: { parecer: string | null; recomendacao: string | null; recomendacao_motivo: string | null }): string | null {
  if (!b.parecer || b.parecer.trim().length < 40) return 'Escreva o parecer para os sócios (pelo menos algumas linhas).';
  if (!b.recomendacao || !(RECOMENDACOES as readonly string[]).includes(b.recomendacao)) return 'Escolha a recomendação para a renovação.';
  if (!b.recomendacao_motivo?.trim()) return 'Diga em uma frase por que recomenda isso.';
  return null;
}
